import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import type { AudienceFilter, AudienceOrder, NewCampaignInput, RecipientQuery } from '../email/types';

const root = ['discovery', 'email'] as const;

export const emailKeys = {
  root,
  status: [...root, 'status'] as const,
  summary: (f: AudienceFilter) => [...root, 'audience', 'summary', f] as const,
  audience: (f: AudienceFilter, page: object) => [...root, 'audience', 'rows', f, page] as const,
  instantlyCampaigns: [...root, 'instantly-campaigns'] as const,
  campaigns: (includeArchived: boolean) => [...root, 'campaigns', { includeArchived }] as const,
  overview: [...root, 'overview'] as const,
  campaign: (id: string) => [...root, 'campaign', id] as const,
  recipients: (id: string, q: RecipientQuery) => [...root, 'campaign', id, 'recipients', q] as const,
};

export function useEmailStatus() {
  const client = useDiscoveryClient();
  return useQuery({ queryKey: emailKeys.status, queryFn: () => client.email.status(), staleTime: 60_000 });
}

export function useAudienceSummary(filter: AudienceFilter) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: emailKeys.summary(filter),
    queryFn: () => client.email.audienceSummary(filter),
    placeholderData: (prev) => prev, // keep the tiles steady while a filter changes
  });
}

export function useAudienceRows(filter: AudienceFilter, page: { limit: number; offset: number; order: AudienceOrder }) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: emailKeys.audience(filter, page),
    queryFn: () => client.email.audience(filter, page),
    placeholderData: (prev) => prev,
  });
}

export function useInstantlyCampaigns(enabled: boolean) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: emailKeys.instantlyCampaigns,
    queryFn: () => client.email.instantlyCampaigns(),
    enabled,
    staleTime: 0, // someone may have just created the campaign in Instantly
  });
}

export function useEmailCampaigns(includeArchived = false) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: emailKeys.campaigns(includeArchived),
    queryFn: () => client.email.campaigns({ includeArchived }),
  });
}

export function useEmailOverview(enabled: boolean) {
  const client = useDiscoveryClient();
  return useQuery({ queryKey: emailKeys.overview, queryFn: () => client.email.overview(), enabled });
}

export function useEmailCampaign(publicId: string) {
  const client = useDiscoveryClient();
  return useQuery({ queryKey: emailKeys.campaign(publicId), queryFn: () => client.email.campaign(publicId) });
}

export function useRecipients(publicId: string, query: RecipientQuery) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: emailKeys.recipients(publicId, query),
    queryFn: () => client.email.recipients(publicId, query),
    placeholderData: (prev) => prev,
  });
}

/** Everything under the email key: a send changes the audience, the lists and the totals at once. */
function useInvalidateEmail() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: root });
}

export function useCreateEmailCampaign() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateEmail();
  return useMutation({ mutationFn: (input: NewCampaignInput) => client.email.createCampaign(input), onSettled: invalidate });
}

export function useRetryEmailCampaign() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateEmail();
  return useMutation({ mutationFn: (publicId: string) => client.email.retryCampaign(publicId), onSettled: invalidate });
}

export function useSyncEmailCampaign() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateEmail();
  return useMutation({ mutationFn: (publicId: string) => client.email.syncCampaign(publicId), onSettled: invalidate });
}

export function useUpdateEmailCampaign() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateEmail();
  return useMutation({
    mutationFn: ({ publicId, patch }: { publicId: string; patch: Parameters<typeof client.email.updateCampaign>[1] }) =>
      client.email.updateCampaign(publicId, patch),
    onSuccess: invalidate,
  });
}

export function useConnectTracking() {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client.email.connectTracking(),
    onSettled: () => qc.invalidateQueries({ queryKey: emailKeys.status }),
  });
}
